/**
 * MetricsClient shared types (PROD-592).
 *
 * The Learning Loop dashboard reads three metric blocks (Live Learning
 * Signals, Suggestion Quality, Personalization Score) plus a per-NBA
 * confidence-over-time HC4 chart from a single `MetricsClient`
 * interface. Demo Mode and Live Mode each implement the interface;
 * the dashboard is mode-agnostic.
 *
 * HC1 two-mode operation: Demo derives deterministically from the
 * in-memory stores; Live queries App Insights via a lazy SDK shim
 * (PROD-589 dynamic-import pattern). The Live shim falls back to Demo
 * when the SDK is not yet bundled (INFRA GATE deferred per PROD-593).
 *
 * HC5: zero em / en dashes anywhere.
 */
import type { TenantId } from '../../state/types';
import type { NbaState } from '../../types/nba';

/**
 * Live Learning Signals event-count vocabulary. The five names match
 * the strict telemetry events that the prototype emits today:
 *
 *   - `override_event`                  - PROD-571 OverrideModal submit
 *   - `mode_switch`                     - PROD-573 mode toggle
 *   - `tenant_change`                   - PROD-568 tenant switcher
 *   - `nba_confidence_adjusted`         - PROD-572 learning-loop service
 *   - `lab_basket_confirmation_required`- PROD-572 lab basket guard
 *
 * The Demo Mode implementation seeds canned counts on top of any live
 * in-memory mutations so the dashboard renders realistic numbers
 * during a clinician demo even without prior interaction.
 */
export const LIVE_LEARNING_EVENT_NAMES = [
  'override_event',
  'mode_switch',
  'tenant_change',
  'nba_confidence_adjusted',
  'lab_basket_confirmation_required',
] as const;

export type LiveLearningEventName = (typeof LIVE_LEARNING_EVENT_NAMES)[number];

/**
 * Time window for `eventCountsByType`. Phase 1 ships a single 7d
 * window per the PROD-592 ticket spec; the type carries the duration
 * so the Live implementation can pass it through to App Insights and
 * the Demo implementation can scale canned counts.
 */
export interface TimeWindow {
  /** Window length in days. Default 7 per the ticket. */
  days: number;
  /**
   * ISO end timestamp (the window covers `[end - days, end]`). When
   * omitted, the implementation uses the current time. Surfaced so
   * tests can pin a deterministic window.
   */
  endIso?: string;
}

export const DEFAULT_TIME_WINDOW: TimeWindow = { days: 7 };

/**
 * One sample on the per-NBA confidence-over-time chart (HC4 ML/RAG
 * "confidence reduction events show as a chart"). Each point is a
 * snapshot taken when a `nba_confidence_adjusted` event fired.
 */
export interface ConfidencePoint {
  /** ISO timestamp when the confidence change was recorded. */
  ts: string;
  /** Confidence value in [0, 1] after the adjustment. */
  confidence: number;
}

/** Tenant-scoped override-rate value in [0, 1]. */
export type OverrideRate = number;

/**
 * One Personalization Score sparkline point per tenant. The Demo
 * implementation derives these from `useOverrideStore.events` length
 * partitioned over the time window.
 */
export interface PersonalizationPoint {
  /** ISO timestamp of the bucket boundary. */
  ts: string;
  /** Override rate at the bucket boundary in [0, 1]. */
  rate: OverrideRate;
}

/**
 * Live Signal Feed event entry (Plan v9 §4.2 prototype rework).
 *
 * One row in the reverse-timeline list rendered by `LiveSignalFeed`. The
 * Demo implementation seeds a deterministic list anchored to the demo
 * epoch so HC1 reproducibility holds; Live Mode reads recent rows from
 * App Insights in `recorded` desc order.
 *
 * The closed `kind` vocabulary mirrors the prototype: encounters,
 * NBA approvals, overrides, PROM signals.
 */
export type LiveSignalKind =
  | 'encounter_indexed'
  | 'nba_approved'
  | 'override_captured'
  | 'prom_signal_ingested';

export interface LiveSignalEntry {
  /** Stable identifier. Used as the React key + data-testid suffix. */
  id: string;
  /** ISO timestamp the signal was recorded. */
  ts: string;
  /** Closed signal-kind discriminator. */
  kind: LiveSignalKind;
  /** Single-line human-readable summary (rendered as the row body). */
  summary: string;
  /** Optional secondary detail (rendered in muted text below the summary). */
  detail?: string;
}

/**
 * Suggestion Quality comparison row (Plan v9 §4.2 prototype rework).
 *
 * One row in the 2-column "Day 1 vs Today" table rendered by
 * `SuggestionQualityComparison`. The `direction` field drives the
 * color-coded delta: `improved` -> status-active, `regressed` ->
 * warning-strong, `unchanged` -> text-tertiary.
 */
export interface SuggestionQualityRow {
  /** Row label (e.g. "NBA Accept Rate"). */
  metric: string;
  /** Day 1 (vanilla LLM) value, formatted for display (e.g. "38%"). */
  day1: string;
  /** Today (post-learning) value, formatted for display (e.g. "79%"). */
  today: string;
  /**
   * Whether the change is good for the clinic. `improved` = green,
   * `regressed` = warning, `unchanged` = neutral.
   */
  direction: 'improved' | 'regressed' | 'unchanged';
}

/**
 * Clinic Intelligence Profile snapshot (Plan v9 §4.2 prototype rework).
 *
 * Rendered by `ClinicIntelligenceProfile`. Per-tenant scope so the
 * Demo Mode fixture differs across the two Phase 1 tenants.
 */
export interface ClinicIntelligenceProfileSummary {
  /** Provider display name (e.g. "Dr. Al-Dhaheri"). */
  providerName: string;
  /** Clinic / pathway label (e.g. "Cardiometabolic Pathway"). */
  clinicLabel: string;
  /** Personalization score in [0, 100]. */
  personalizationScore: number;
  /** Delta from Day 1, in points. Always non-negative for the prototype. */
  personalizationDelta: number;
  /** Four precision metrics rendered as a small grid. */
  precisionMetrics: ReadonlyArray<{
    /** Metric label (e.g. "NBA Precision"). */
    label: string;
    /** Pre-formatted value (e.g. "87%"). */
    value: string;
    /** Direction tag for color coding. */
    direction: 'improved' | 'regressed' | 'unchanged';
  }>;
}

/**
 * "What this clinic's AI has learned" learning bullet (Plan v9 §4.2).
 *
 * Three categorical learnings (preference, protocol, outcome) rendered
 * by `WhatTheAiLearned`. The `category` is a closed vocabulary so the
 * UI can label each bullet consistently.
 */
export type LearningCategory = 'preference' | 'protocol' | 'outcome';

export interface LearningBullet {
  /** Closed category label. */
  category: LearningCategory;
  /** Bullet body (one sentence). */
  text: string;
}

/**
 * Azure Stack footer row (Plan v9 §4.2 prototype rework).
 *
 * One row in the table rendered by `AzureStackTable`. The `mono` flag
 * marks values that should render in monospace (e.g. embedding model
 * name `ada-002`, search index `scrips-cc-index`).
 */
export interface AzureStackRow {
  /** Row label (e.g. "Embedding model"). */
  label: string;
  /** Row value (e.g. "ada-002", "847", "Never needed"). */
  value: string;
  /** When true, render the value in monospace. */
  mono: boolean;
}

/**
 * Materialized view of every metric block plus the HC4 chart, fetched
 * in one round trip so the dashboard can render in a single React
 * commit. The Live Mode implementation may parallelize the underlying
 * App Insights queries; the snapshot is the contract surface.
 */
export interface MetricsSnapshot {
  /** Window the snapshot was computed against. */
  window: TimeWindow;
  /** ISO timestamp the snapshot was assembled. */
  fetchedAt: string;
  /** Block 1: total event counts by type. */
  eventCounts: Record<LiveLearningEventName, number>;
  /** Block 2a: NBA candidates by lifecycle state. */
  nbaStateRatio: Record<NbaState, number>;
  /** Block 2b: per-NBA before / after confidence (override fired path). */
  beforeAfterByNba: Record<string, { before: number; after: number }>;
  /** Block 3: per-tenant override rate (single number per tenant). */
  overrideRateByTenant: Record<TenantId, OverrideRate>;
  /** Block 3 trend: per-tenant sparkline points. */
  overrideRateTrend: Record<TenantId, PersonalizationPoint[]>;
  /** HC4 chart: per-NBA confidence-over-time. */
  confidenceTimelinePerNba: Record<string, ConfidencePoint[]>;
  /**
   * Live Signal Feed entries (Plan v9 §4.2). Newest entries are FIRST
   * (reverse-timeline). Demo Mode seeds a deterministic list; Live
   * pulls recent rows from App Insights in `recorded` desc order.
   */
  liveSignals: LiveSignalEntry[];
  /**
   * Total events indexed in the search index (Plan v9 §4.2). Surfaced
   * in the Live Signal Feed header and the Azure Stack footer.
   */
  eventsIndexedCount: number;
  /**
   * Day 1 vs Today suggestion-quality comparison rows (Plan v9 §4.2).
   * Rendered by `SuggestionQualityComparison`.
   */
  suggestionQualityRows: SuggestionQualityRow[];
  /**
   * Per-tenant Clinic Intelligence Profile summary (Plan v9 §4.2).
   * Tenants without a fixture are absent from this map; the view
   * falls back to the active tenant's entry.
   */
  clinicIntelligenceByTenant: Record<TenantId, ClinicIntelligenceProfileSummary>;
  /**
   * "What this clinic's AI has learned" bullets (Plan v9 §4.2). Three
   * entries, one per `LearningCategory`. Per-tenant scope keeps Demo
   * Mode fixtures tenant-specific.
   */
  learningBulletsByTenant: Record<TenantId, LearningBullet[]>;
  /**
   * Azure Stack footer rows (Plan v9 §4.2). Includes embedding model,
   * search index, events indexed, provenance writes, retraining
   * status. Rendered by `AzureStackTable`.
   */
  azureStackRows: AzureStackRow[];
  /**
   * Whether the snapshot was sourced from a real telemetry sink. Demo
   * always returns `false`; Live returns `true` when the App Insights
   * SDK is bundled and `false` when it falls back to Demo.
   */
  hasLiveData: boolean;
}

/**
 * Default refresh interval for the dashboard. The PROD-592 ticket
 * requires this to be configurable; the store carries the runtime
 * value.
 */
export const DEFAULT_REFRESH_INTERVAL_MS = 60_000;
